import { useEffect, useState } from "react";
import { Backdrop, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import ToastMessage from "../../utils/mui/righttoptoast";
import api from "../../api/api";
import { athenaErrorMessage } from "../../api/athenaConfirm";
import { faxDocumentFile } from "../faxApi";
import { faxAiAthenaClass, faxAiMatchHint } from "../faxAi";
import { useFaxAiSuggestedPatient } from "../faxAiParts";
import { greyuploadicon } from "../../landingscreen/patientcharts/tabs/Document/svgs";
import { searchicongrey } from "../../colorcoding/svg/iconsvg";
import { useRef } from "react";
import { getPracticeId, practiceFaxProfile } from "../../config/practice";

/* ------------------ CONSTANTS ------------------ */

// Class the modal opens on when the fax has no AI read (per practice; staff can
// always switch Imaging / Clinical document).
export function defaultAthenaCategory() {
  const c = practiceFaxProfile()?.athenaDefaultCategory;
  return c === "CLINICAL" ? "CLINICAL" : "IMAGING";
}

const CLINICAL_SUB_TYPES = [
  "CLINICALDOCUMENT",
  "ADMISSIONDISCHARGE",
  "CONSULTNOTE",
  "MENTALHEALTH",
  "OPERATIVENOTE",
  "URGENTCARE",
];

/* ------------------ COMPONENT ------------------ */

/*
 * aiRow: the open fax as the list has it now (with its latest AI read,
 * faxAi.js). When the read matched one chart, that chart is pre-selected
 * ("Suggested from the fax"), and the label picks the class the modal opens
 * on. Both are only a pre-selection: typing in Patient or picking another
 * class replaces them, and nothing is sent until staff press Upload. No
 * read, several charts or none: the patient search starts empty, as before.
 * faxtable.jsx mounts a fresh modal for each open.
 */
const pickOf = (p) =>
  p ? { patientid: p.patientid, patientinfo: `${p.name} - ${p.patientid}`, dob: p.dob || "" } : null;

// The same fax (fax_master id, else the list's message id). The pre-fill is
// read from aiRow only when it is the fax being uploaded; otherwise from
// selectedFax itself, so a patient can never come from another fax's read.
const sameFax = (a, b) => {
  if (!a || !b) return false;
  const has = (v) => v !== undefined && v !== null && v !== "";
  if (has(a.faxid) && has(b.faxid)) return String(a.faxid) === String(b.faxid);
  return has(a.id) && has(b.id) && String(a.id) === String(b.id);
};

const SendToAthena = ({ open, onClose, selectedFax, aiRow }) => {
  const practiseid = getPracticeId();

  const faxRow = open && selectedFax ? (sameFax(aiRow, selectedFax) ? aiRow : selectedFax) : null;
  const classFromFax = faxAiAthenaClass(faxRow);
  const patientFromFax = useFaxAiSuggestedPatient(faxRow);
  const pickFromFax = pickOf(patientFromFax);
  // Set once staff edit the field / class: the AI read never overrides them.
  const patientTouchedRef = useRef(false);
  const classTouchedRef = useRef(false);

  const [loading, setLoading] = useState(false);
  // Set after a successful upload: the modal stays up for a second with the
  // toast, and Upload must not send the same fax again meanwhile.
  const [sent, setSent] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [openToast, setOpenToast] = useState(false);

  const [patientSearch, setPatientSearch] = useState(() => pickFromFax?.patientinfo || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(() => pickFromFax);
  // The id pre-selected from the fax (null once staff pick or type).
  const [prefilledPatientId, setPrefilledPatientId] = useState(() =>
    pickFromFax ? String(pickFromFax.patientid) : null
  );
const searchTimeoutRef = useRef(null);

  const [category, setCategory] = useState(() => classFromFax?.category || defaultAthenaCategory());
  const [clinicalSubType, setClinicalSubType] =
    useState(() => classFromFax?.subclass || "CLINICALDOCUMENT");
  const [internalNote, setInternalNote] = useState("");

  // The chart can arrive after the modal opened (v2 lists ids only; the name
  // comes from GET fax/{id}/ai): until staff touch the field, it follows the
  // fax's suggestion.
  const pickKey = pickFromFax ? `${pickFromFax.patientid}:${pickFromFax.patientinfo}:${pickFromFax.dob}` : "";
  useEffect(() => {
    if (patientTouchedRef.current) return;
    setSelectedPatient(pickFromFax);
    setPatientSearch(pickFromFax ? pickFromFax.patientinfo : "");
    setPrefilledPatientId(pickFromFax ? String(pickFromFax.patientid) : null);
    // pickKey says when the suggestion changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickKey]);

  // Likewise the class, when the label arrives while the modal is open.
  const classKey = classFromFax ? `${classFromFax.category}:${classFromFax.subclass || ""}` : "";
  useEffect(() => {
    if (!classFromFax || classTouchedRef.current) return;
    setCategory(classFromFax.category);
    if (classFromFax.subclass) setClinicalSubType(classFromFax.subclass);
    // classKey says when the class changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classKey]);

  if (!open) return null;

  const patientIsFromFax =
    !!selectedPatient && prefilledPatientId !== null && String(selectedPatient.patientid) === prefilledPatientId;
  const patientHint = patientIsFromFax ? faxAiMatchHint(patientFromFax?.confidence) : "";
  const classHint =
    classFromFax &&
    classFromFax.label &&
    category === classFromFax.category &&
    (category !== "CLINICAL" || clinicalSubType === classFromFax.subclass)
      ? classFromFax.label
      : "";

  /* ------------------ HELPERS ------------------ */

  const handleToastClose = () => setOpenToast(false);

  const resetForm = () => {
    setPatientSearch("");
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedPatient(null);
    setPrefilledPatientId(null);
    patientTouchedRef.current = true;
    classTouchedRef.current = true;
    setCategory(defaultAthenaCategory());
    setClinicalSubType("CLINICALDOCUMENT");
    setInternalNote("");
    setSent(false);
  };

  /* ------------------ PATIENT SEARCH ------------------ */

const handlePatientSearch = (value) => {
  patientTouchedRef.current = true;
  setPrefilledPatientId(null);
  setPatientSearch(value);
  setSelectedPatient(null);

  // Clear old timer
  if (searchTimeoutRef.current) {
    clearTimeout(searchTimeoutRef.current);
  }

  if (!value.trim()) {
    setSuggestions([]);
    setShowSuggestions(false);
    return;
  }

  // Debounce API call
  searchTimeoutRef.current = setTimeout(async () => {
    try {
      const res = await api.post(
        `/v1/${practiseid}/autocomplete/dssearch`,
        { searchtxt: value }
      );

      if (Array.isArray(res.data)) {
        setSuggestions(res.data.slice(0, 3));
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error("Search error:", error);
    }
  }, 300); // ✅ 300ms debounce
};


  const handleSuggestionSelect = (item) => {
    patientTouchedRef.current = true;
    setPrefilledPatientId(null);
    setPatientSearch(item.patientinfo || "");
    setSelectedPatient(item);
    setShowSuggestions(false);
  };

  /* ------------------ FAX → FILE ------------------ */

  // The fax PDF from the v2 document route with the staff token (faxApi.js),
  // not the hub's viewdoc and the dallas proxy.
  const fetchFaxPdfAsFile = (faxItem) =>
    faxDocumentFile(faxItem, `Fax_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`);

  /* ------------------ SUBMIT ------------------ */

  // Declared before submit, which reads it (TDZ).
  const isUploadDisabled =
    !selectedPatient || !selectedFax || loading || sent;

  const submit = async () => {
    if (isUploadDisabled) return;
    try {
      setLoading(true);

      const file = await fetchFaxPdfAsFile(selectedFax);

      const formData = new FormData();
      formData.append("patientid", selectedPatient.patientid);
      formData.append("departmentid", 1);
      formData.append("internalnote", internalNote || "");
      formData.append("file", file);
      // Recorded with the Athena id: the gateway files a fax into a chart once and
      // answers a repeat with the copy already there (2026-10-06).
      const faxRef = selectedFax?.faxid ?? selectedFax?.id;
      if (faxRef !== undefined && faxRef !== null && faxRef !== "") formData.append("faxid", String(faxRef));

      if (category === "CLINICAL") {
        formData.append("documentsubclass", clinicalSubType);

        await api.post(
          `/v1/${practiseid}/fax-documents/add-clinical-document`,
          formData,
          {
            headers: {
              // The api client defaults to JSON, which mangles FormData — the
              // server then 400s on "required fields". Same override as the
              // imaging call below.
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        formData.append("autoclose", true);
        formData.append("documenttypeid", "");
        formData.append("observationtime", "");
        formData.append("observationdate", "");
        formData.append("attachmenttype", "");

       await api.post(
        `/v1/${practiseid}/fax-documents/add-imaging-result`,
        formData,
        {
          headers: {
            // ✅ IMPORTANT
            "Content-Type": "multipart/form-data",
          },
        }
      );
    
      }

      setSent(true);
      setToastMessage("Fax document uploaded successfully");
      setToastSeverity("success");
      setOpenToast(true);

      setTimeout(() => {
        resetForm();
       handleCloseModal();

      }, 1000);
    } catch (error) {
      console.error(error);
      // "already in this patient's chart" / "Athena did not answer" read better than a bare failure
      setToastMessage(athenaErrorMessage(error, "Upload failed"));
      setToastSeverity("error");
      setOpenToast(true);
    } finally {
      setLoading(false);
    }
  };
const handleCloseModal = () => {
  setOpenToast(false);
  setToastMessage("");
  setToastSeverity("success");
  resetForm();
  onClose();
};


  /* ------------------ UI ------------------ */

  return (
    <>
      <Backdrop open={loading} sx={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className="fixed inset-0 z-50 bg-black/40">
        <div className="flex min-h-screen items-center justify-center">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-lg">

            {/* Header */}
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div className="flex items-center gap-2 text-lg font-semibold">
                {greyuploadicon}
                Send to Athena
              </div>
              <button  onClick={handleCloseModal}>✕</button>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4 text-left">

              {/* Patient Search */}
              <div>
                <label className="text-sm font-semibold">
                  Patient
                </label>
                <div className="relative mt-1">
                  <div className="flex items-center gap-2 rounded-md border bg-[#F9FAFB] px-3 py-2">
                    {searchicongrey}
                    <input
                      type="text"
                      value={patientSearch}
                      onChange={(e) =>
                        handlePatientSearch(e.target.value)
                      }
                      // a suggested chart is selected whole, so typing replaces it
                      onFocus={(e) => {
                        if (patientIsFromFax) e.target.select();
                      }}
                      placeholder="Search patient"
                      aria-label="Patient"
                      className="flex-1 bg-transparent text-sm outline-none"
                    />
                  </div>

                  {patientIsFromFax && (
                    <div className="mt-1 text-xs text-[#7E7E7E]" data-testid="sta-patient-from-fax">
                      Suggested from the fax — change if wrong
                      {selectedPatient.dob ? ` · DOB ${selectedPatient.dob}` : ""}
                      {patientHint ? ` (${patientHint})` : ""}
                    </div>
                  )}

                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow">
                      {suggestions.map((item, i) => (
                        <div
                          key={i}
                          onClick={() =>
                            handleSuggestionSelect(item)
                          }
                          className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
                        >
                          {item.patientinfo}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Category */}
           <div>
  <label className="text-sm font-semibold">Category</label>

  <div className="mt-2 flex gap-2">
    {["IMAGING", "CLINICAL"].map((t) => (
      <button
        key={t}
        type="button"
        aria-pressed={category === t}
        onClick={() => {
          classTouchedRef.current = true;
          setCategory(t);
        }}
        className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
          category === t
            ? "border-gray-800 bg-gray-100 text-gray-800"
            : "border-gray-300 text-gray-600 hover:bg-gray-50"
        }`}
      >
        {t}
      </button>
    ))}
  </div>
  {classHint && (
    <div className="mt-1 text-xs text-[#7E7E7E]" data-testid="sta-class-from-fax">
      From the AI label: {classHint}
    </div>
  )}
</div>


              {/* Clinical subtype */}
              {category === "CLINICAL" && (
                <div>
                  <label className="text-sm font-semibold">
                    Clinical Document Type
                  </label>
                  <select
                    value={clinicalSubType}
                    onChange={(e) => {
                      classTouchedRef.current = true;
                      setClinicalSubType(e.target.value);
                    }}
                    aria-label="Clinical Document Type"
                    className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                  >
                    {CLINICAL_SUB_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t.replace(/([A-Z])/g, " $1")}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Internal Note */}
              <div>
                <label className="text-sm font-semibold">
                  Internal Note
                </label>
                <textarea
                  rows={3}
                  value={internalNote}
                  onChange={(e) =>
                    setInternalNote(e.target.value)
                  }
                  placeholder="Add internal note (optional)"
                  className="mt-1 w-full rounded-md border px-3 py-2 text-sm resize-none"
                />
              </div>

            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button
           onClick={handleCloseModal}
                className="rounded-md border px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                disabled={isUploadDisabled}
                onClick={submit}
                className={`rounded-md px-4 py-2 text-sm text-white ${
                  isUploadDisabled
                    ? "bg-gray-300"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                Upload
              </button>
            </div>

          </div>
        </div>
      </div>

      <ToastMessage
        message={toastMessage}
        severity={toastSeverity}
        open={openToast}
        handleClose={handleToastClose}
      />
    </>
  );
};

export default SendToAthena;
